import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Container,
  CircularProgress,
  Alert,
  Button,
  CardActions,
  Divider,
  Chip,
  Stack
} from '@mui/material';
import { motion } from 'framer-motion';
import DOMPurify from 'dompurify';
import { Link, useParams } from 'react-router-dom';
import { config } from '../config';
import { getDerivativeImageUrl, getImageUrl } from '../utils/imageUrl';

const MotionCard = motion(Card);

function News() {
  const { articleId } = useParams();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [skyNewsItems, setSkyNewsItems] = useState([]);

  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      try {
        const [response, skyResponse] = await Promise.all([
          fetch(config.endpoints.news),
          fetch(`${config.endpoints.skyNews}?limit=4`).catch(() => null)
        ]);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        if (skyResponse?.ok) {
          const skyData = await skyResponse.json();
          setSkyNewsItems((skyData.items || []).slice(0, 4));
        }
        
        // Process all articles
        const processedArticles = data.map(article => ({
          ...article,
          content: processContentImages(article.content),
          image: article.image ? getImageUrl(article.image) : null
        }));
        
        setArticles(processedArticles);
        setError(null);
      } catch (error) {
        console.error('Error fetching articles:', error);
        setError('Failed to load articles. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  // Process images in article content
  const processContentImages = (content) => {
    if (!content) return '';

    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const images = doc.getElementsByTagName('img');

    for (let img of images) {
      const src = img.getAttribute('src');
      if (src) {
        img.setAttribute('src', getDerivativeImageUrl(src, 'medium'));
        img.setAttribute('data-original-src', getImageUrl(src));
        img.setAttribute('loading', 'lazy');
        img.setAttribute('decoding', 'async');
      }
    }

    return doc.body.innerHTML;
  };

  // Extract first image URL from article content
  const extractFirstImage = (content) => {
    const div = document.createElement('div');
    div.innerHTML = content;
    const img = div.querySelector('img');
    if (!img) return null;
    
    let imgSrc = img.src;
    // If it's a relative URL or from astro-vino domain, ensure it has /api prefix
    if (imgSrc.includes('uploads/') || imgSrc.includes('astro-vino.co.uk')) {
      const urlParts = imgSrc.split('/uploads/');
      if (urlParts.length === 2) {
        imgSrc = `/api/uploads/${urlParts[1]}`;
      }
    }
    return getImageUrl(imgSrc);
  };

  // Extract text content without HTML tags and limit to a preview
  const createPreview = (content, maxLength = 150) => {
    if (!content) return '';
    const div = document.createElement('div');
    div.innerHTML = content;
    const text = div.textContent || div.innerText || '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  // If we have an articleId, show the full article
  if (articleId) {
    const article = articles.find(a => a.id === articleId);
    if (!article) {
      return (
        <Container sx={{ py: 4 }}>
          <Alert severity="error">Article not found</Alert>
        </Container>
      );
    }

    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <MotionCard
          sx={{
            background: 'rgba(255,255,255,0.05)',
            overflow: 'hidden'
          }}
        >
          <CardContent>
            <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'primary.main' }}>
              {article.title}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {new Date(article.createdAt).toLocaleDateString()}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="body2"
                component="div"
                sx={{
                  color: 'text.secondary',
                  '& p, & div, & span': {
                    fontSize: 'inherit',
                    margin: '0.5em 0'
                  },
                  '& img': {
                    maxWidth: '100%',
                    height: 'auto',
                    display: 'block',
                    margin: '1em auto'
                  }
                }}
              >
                <div 
                  dangerouslySetInnerHTML={{ 
                    __html: DOMPurify.sanitize(article.content)
                  }} 
                />
              </Typography>
            </Box>
          </CardContent>
          <CardActions sx={{ p: 2, pt: 0 }}>
            <Button 
              component={Link} 
              to="/news"
              variant="contained"
              color="primary"
              size="small"
            >
              Back to News
            </Button>
          </CardActions>
        </MotionCard>
      </Container>
    );
  }

  // Show list of article previews
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', mb: 1.5 }}>
        Astro Vino Articles
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>My own updates, observing sessions, equipment tests and image stories stay here first.</Typography>

      {skyNewsItems.length > 0 && (
        <Box sx={{ mb: 4, p: { xs: 2, md: 2.5 }, border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2, background: 'rgba(255,255,255,0.04)' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ color: 'primary.light', fontWeight: 850, fontSize: { xs: '1rem', md: '1.12rem' } }}>External astronomy links</Typography>
              <Typography variant="body2" color="text.secondary">Referenced external astronomy and astrophotography reading, kept separate from Astro Vino articles.</Typography>
            </Box>
            <Chip size="small" label="External links" />
          </Stack>
          <Grid container spacing={1.5}>
            {skyNewsItems.map((item) => (
              <Grid item xs={12} md={6} key={item.id}>
                <Card sx={{ height: '100%', overflow: 'hidden', background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  {item.imageUrl && (
                    <Box component="img" src={item.thumbnailUrl ? `${config.API_URL}${item.thumbnailUrl}` : item.imageUrl} alt="" loading="lazy" sx={{ width: '100%', height: 118, display: 'block', objectFit: 'contain', bgcolor: 'rgba(3,8,18,0.92)', borderBottom: '1px solid rgba(255,255,255,0.08)' }} />
                  )}
                  <CardContent sx={{ pb: 1 }}>
                    <Typography sx={{ fontWeight: 850, fontSize: '0.94rem', lineHeight: 1.25 }}>{item.title}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.7 }}>{item.source} · {new Date(item.publishedAt).toLocaleDateString()}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '0.82rem', lineHeight: 1.45 }}>{item.summary}</Typography>
                  </CardContent>
                  <CardActions sx={{ px: 2, pt: 0 }}>
                    <Button href={item.url} target="_blank" rel="noopener noreferrer" size="small">Read at source</Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
      <Grid container spacing={4}>
        {articles.map((article) => {
          const preview = createPreview(article.content);

          return (
            <Grid item xs={12} key={article.id}>
              <MotionCard
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  background: 'rgba(255,255,255,0.05)',
                  mx: 2,
                  overflow: 'hidden'
                }}
                whileHover={{
                  scale: 1.02,
                  transition: { duration: 0.2 },
                }}
              >
                {(article.image || extractFirstImage(article.content)) && (
                  <Box sx={{ 
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 2,
                    width: { xs: '100%', md: '30%' },
                    minWidth: { md: '250px' },
                    maxWidth: { md: '300px' }
                  }}>
                    <CardMedia
                      component="img"
                      sx={{
                        width: '100%',
                        aspectRatio: '16 / 9',
                        maxHeight: { xs: 220, md: 200 },
                        objectFit: 'contain',
                        objectPosition: 'center',
                        borderRadius: '6px',
                        background: 'rgba(3,8,18,0.92)',
                        border: '1px solid rgba(255,255,255,0.08)'
                      }}
                      image={article.image || getDerivativeImageUrl(extractFirstImage(article.content), 'thumb')}
                      loading="lazy"
                      alt={article.title}
                    />
                  </Box>
                )}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  flex: 1,
                  justifyContent: 'space-between'
                }}>
                  <CardContent sx={{ flex: '1 0 auto' }}>
                    <Typography 
                      variant="h5" 
                      component="h2" 
                      gutterBottom
                      sx={{ 
                        color: 'primary.main',
                        fontSize: { xs: '1.25rem', md: '1.5rem' }
                      }}
                    >
                      {article.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {new Date(article.createdAt).toLocaleDateString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {preview}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button 
                      component={Link}
                      to={`/news/${article.id}`}
                      variant="contained" 
                      color="primary"
                      size="small"
                    >
                      Read More
                    </Button>
                  </CardActions>
                </Box>
              </MotionCard>
            </Grid>
          );
        })}
      </Grid>
    </Container>
  );
}

export default News;
